import { useState } from "react";
import type { Country } from "@shared/types.js";
import type { SnapshotMeta } from "@shared/types.js";
import { api } from "../api.js";
import { fnum, fstamp } from "../lib/format.js";

/** Rename or delete stored snapshots. Deleting is confirmed inline. */
export function ManageModal({
  snapshots, currentId, onClose, onChanged, country,
}: {
  country: Country;
  snapshots: SnapshotMeta[];
  currentId: number | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [err, setErr] = useState("");

  const save = async (id: number) => {
    try {
      await api.rename(id, draft.trim() || null, country);
      setEditing(null);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const remove = async (id: number) => {
    try {
      await api.remove(id, country);
      setConfirmId(null);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="modal-back" onClick={onClose} role="presentation">
      <div
        className="modal" role="dialog" aria-modal="true" aria-label="Manage snapshots"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mhead">
          <h3><i className="bi bi-archive hico" aria-hidden="true" />Stored snapshots</h3>
          <span className="sub muted" style={{ marginLeft: "auto", fontSize: 11.5 }}>
            {snapshots.length} stored
          </span>
        </div>
        <div className="mbody" style={{ gap: 8 }}>
          {err && <div className="err">{err}</div>}
          {snapshots.length === 0 && <div className="empty">No snapshots stored yet.</div>}

          {snapshots.map((s) => (
            <div
              key={s.id}
              style={{
                border: "1px solid var(--line)", borderRadius: 8, padding: "10px 12px",
                display: "flex", flexDirection: "column", gap: 6,
              }}
            >
              {editing === s.id ? (
                <div className="rowflex">
                  <input
                    type="text" value={draft} autoFocus style={{ flex: 1 }}
                    placeholder="Label for this snapshot"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") void save(s.id); }}
                  />
                  <button className="btn btn--sm btn--primary" type="button" onClick={() => void save(s.id)}>
                    Save
                  </button>
                  <button className="btn btn--sm" type="button" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="rowflex">
                  <b style={{ flex: 1, fontSize: 12.5 }}>
                    {s.label ?? s.sourceFile}
                    {s.id === currentId && <> <span className="pill p-done">viewing</span></>}
                    {s.isSeed && <> <span className="pill p-none">baseline</span></>}
                  </b>
                  <button
                    className="btn btn--sm" type="button"
                    onClick={() => { setEditing(s.id); setDraft(s.label ?? ""); }}
                  >
                    Rename
                  </button>
                  {confirmId === s.id ? (
                    <>
                      <button className="btn btn--sm" type="button" onClick={() => void remove(s.id)}
                        style={{ color: "var(--sev-high)", borderColor: "var(--sev-high)" }}>
                        Confirm delete
                      </button>
                      <button className="btn btn--sm" type="button" onClick={() => setConfirmId(null)}>
                        Keep
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn--sm" type="button"
                      disabled={snapshots.length === 1}
                      title={snapshots.length === 1 ? "The last snapshot cannot be deleted" : undefined}
                      onClick={() => setConfirmId(s.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
              <div className="muted" style={{ fontSize: 11 }}>
                {s.sourceFile} &middot; parsed {fstamp(s.generatedAt)} &middot;{" "}
                {fnum(s.recordCount)} records &middot; {fnum(s.findingCount)} findings
              </div>
            </div>
          ))}
        </div>
        <div className="mfoot">
          <button className="btn" type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
